/**
 * API Service pour l'intégration Serge
 *
 * Tous les appels passent par le proxy Jean-Louis (/api/serge/**)
 * qui forward vers l'API Serge avec gestion de l'authentification.
 */

import axios from 'axios'
import { useAuthStore } from '../stores/authStore.js'

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'

const sergeApi = axios.create({
  baseURL: `${API_URL}/api/serge`,
  headers: { 'Content-Type': 'application/json' },
})

// Intercepteur pour ajouter le JWT
sergeApi.interceptors.request.use(
  (config) => {
    try {
      const token = useAuthStore.getState()?.token || localStorage.getItem('auth_token')
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (_) {
      // noop
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Intercepteur pour gérer les erreurs 401
sergeApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    if (status === 401) {
      try {
        useAuthStore.getState()?.logout?.()
      } catch (_) {
        // noop
      }
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// ============================
// Types de livrables (pour référence)
// ============================

export const DELIVERABLE_TYPES = {
  spec_functional: 'Spécifications fonctionnelles',
  spec_technical: 'Spécifications techniques',
  diagram_class: 'Diagramme de classe',
  diagram_sequence: 'Diagramme de séquence',
  diagram_architecture: "Diagramme d'architecture",
  user_stories: 'User Stories',
  risk_register: 'Registre des risques',
  workflow: 'Workflow',
  market_study: 'Étude de marché',
  technical_dossier: 'Dossier technique',
  communication_plan: 'Plan de communication',
  sales_strategy: 'Stratégie commerciale',
  budget: 'Budget prévisionnel',
  gantt: 'Planning Gantt',
  gantt_sheets: 'Planning Gantt (Google Sheets)',
  planning: 'Planning prévisionnel',
  cgv: 'CGV',
  cgu: 'CGU',
  legal_notice: 'Mentions légales',
  privacy_policy: 'Politique de confidentialité',
  software_licenses: 'Liste des logiciels et licences',
  master_service_agreement: 'Contrat cadre de prestation',
  payment_schedule: 'Échéancier de paiement',
  demo: 'Démo visuelle',
}

// Alias pour compatibilité TypeScript
export const DELIVERABLE_LABELS = DELIVERABLE_TYPES

// Helper pour obtenir le label d'un livrable
export const getDeliverableLabel = (type) => DELIVERABLE_TYPES[type] || type

export const PROJECT_STEPS = ['import', 'analysis', 'clarification', 'risks', 'export']

export const STEP_LABELS = {
  import: 'Import',
  analysis: 'Analyse',
  clarification: 'Clarification',
  risks: 'Risques',
  export: 'Export',
}

// ============================
// Health Check
// ============================

export const sergeHealthCheck = async () => {
  const { data } = await sergeApi.get('/health')
  return data
}

// ============================
// Projects
// ============================

/**
 * Liste tous les projets Serge de l'utilisateur
 */
export const getSergeProjects = async () => {
  const { data } = await sergeApi.get('/projects')
  return data
}

/**
 * Récupère un projet Serge par ID
 */
export const getSergeProject = async (projectId) => {
  const { data } = await sergeApi.get(`/projects/${projectId}`)
  return data
}

/**
 * Crée un nouveau projet Serge
 * @param {Object} payload - { name, description, prospect_ids?, prospect_group_ids?, prospect_role?, group_role? }
 */
export const createSergeProject = async (payload) => {
  const { data } = await sergeApi.post('/projects', payload)
  return data
}

/**
 * Met à jour un projet Serge
 */
export const updateSergeProject = async (projectId, payload) => {
  const { data } = await sergeApi.put(`/projects/${projectId}`, payload)
  return data
}

/**
 * Supprime un projet Serge
 */
export const deleteSergeProject = async (projectId) => {
  const { data } = await sergeApi.delete(`/projects/${projectId}`)
  return data
}

// ============================
// Project Steps Context
// ============================

/**
 * Récupère le contexte complet des étapes d'un projet
 * Retourne: { projectId, projectName, currentStep, context, stepsStatus }
 */
export const getSergeProjectContext = async (projectId) => {
  const { data } = await sergeApi.get(`/projects/${projectId}/steps`)
  return data
}

/**
 * Lance l'analyse IA sur un projet
 */
export const runSergeAnalysis = async (projectId) => {
  const { data } = await sergeApi.post(`/projects/${projectId}/steps/analysis/run`)
  return data
}

/**
 * Sauvegarde une réponse de clarification
 */
export const saveServeClarificationAnswer = async (projectId, questionId, answer) => {
  const { data } = await sergeApi.post(`/projects/${projectId}/steps/clarification/answer`, {
    questionId,
    answer,
  })
  return data
}

/**
 * Valide l'étape de clarification
 */
export const completeSergeClarification = async (projectId) => {
  const { data } = await sergeApi.post(`/projects/${projectId}/steps/clarification/complete`)
  return data
}

/**
 * Identifie les risques du projet
 */
export const identifySergeRisks = async (projectId) => {
  const { data } = await sergeApi.post(`/projects/${projectId}/steps/risks/identify`)
  return data
}

/**
 * Récupère les livrables disponibles pour un projet
 */
export const getSergeAvailableDeliverables = async (projectId) => {
  const { data } = await sergeApi.get(`/projects/${projectId}/steps/export/deliverables`)
  return data
}

/**
 * Génère un livrable
 * @param {string} type - Type de livrable (spec_functional, diagram_class, etc.)
 * @param {number} legalEntityId - ID de l'entité légale (optionnel)
 * @param {Object} extraData - Données supplémentaires (optionnel)
 */
export const generateSergeDeliverable = async (projectId, type, legalEntityId, extraData = null) => {
  const payload = {
    type,
    legalEntityId,
    ...extraData,
  }
  const { data } = await sergeApi.post(`/projects/${projectId}/steps/export/generate`, payload)
  return data
}

/**
 * Récupère le contexte complet pour n8n
 */
export const getSergeN8nContext = async (projectId) => {
  const { data } = await sergeApi.get(`/projects/${projectId}/steps/export/context`)
  return data
}

/**
 * Met à jour le contexte d'un projet
 */
export const updateSergeContext = async (projectId, context) => {
  const { data } = await sergeApi.put(`/projects/${projectId}/steps/context`, { context })
  return data
}

/**
 * Réinitialise le contexte d'un projet
 */
export const resetSergeContext = async (projectId) => {
  const { data } = await sergeApi.delete(`/projects/${projectId}/steps/context`)
  return data
}

// ============================
// Documents
// ============================

/**
 * Liste les documents d'un projet
 */
export const getSergeDocuments = async (projectId) => {
  const { data } = await sergeApi.get(`/documents`, { params: { project: projectId } })
  return data
}

/**
 * Upload un document vers Serge
 */
export const uploadSergeDocument = async (projectId, file) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('projectId', String(projectId))

  const { data } = await sergeApi.post('/parse', formData, {
    headers: { 'Content-Type': undefined }, // Let axios set multipart boundary
  })
  return data
}

/**
 * Supprime un document
 */
export const deleteSergeDocument = async (documentId) => {
  const { data} = await sergeApi.delete(`/documents/${documentId}`)
  return data
}

// ============================
// Deliverables
// ============================

/**
 * Récupère les types de livrables disponibles
 */
export const getSergeDeliverableTypes = async () => {
  const { data } = await sergeApi.get('/deliverables/types')
  return data
}

/**
 * Récupère l'historique d'un type de livrable pour un projet
 */
export const getSergeDeliverableHistory = async (projectId, type) => {
  const { data } = await sergeApi.get(`/deliverables/project/${projectId}/type/${type}/history`)
  return data
}

/**
 * Récupère un livrable par ID
 */
export const getSergeDeliverable = async (deliverableId) => {
  const { data } = await sergeApi.get(`/deliverables/${deliverableId}`)
  return data
}

/**
 * Met à jour un livrable
 * @param {Object} updates - { content?, status?, metadata? }
 */
export const updateSergeDeliverable = async (deliverableId, updates) => {
  const { data } = await sergeApi.put(`/deliverables/${deliverableId}`, updates)
  return data
}

// ============================
// Prospect Associations (Jean-Louis layer)
// ============================

/**
 * Liste les prospects associés à un projet Serge
 */
export const getSergeProjectProspects = async (projectId) => {
  const { data } = await sergeApi.get(`/projects/${projectId}/prospects`)
  return data
}

/**
 * Associe des prospects à un projet Serge
 * @param {Array} prospectIds - IDs des prospects à associer
 * @param {string} role - Rôle (client, stakeholder, technical_contact, etc.)
 */
export const associateProspectsToSergeProject = async (projectId, prospectIds, role = 'client') => {
  const { data } = await sergeApi.post(`/projects/${projectId}/prospects`, {
    prospect_ids: prospectIds,
    role,
  })
  return data
}

/**
 * Dissocie un prospect d'un projet Serge
 */
export const dissociateProspectFromSergeProject = async (projectId, prospectId) => {
  const { data } = await sergeApi.delete(`/projects/${projectId}/prospects/${prospectId}`)
  return data
}

// ============================
// Group Associations (Jean-Louis layer)
// ============================

/**
 * Liste les groupes de prospects associés à un projet Serge
 */
export const getSergeProjectGroups = async (projectId) => {
  const { data } = await sergeApi.get(`/projects/${projectId}/groups`)
  return data
}

/**
 * Associe des groupes de prospects à un projet Serge
 * @param {Array} groupIds - IDs des groupes à associer
 * @param {string} role - Rôle (stakeholders, target_audience, decision_makers, etc.)
 */
export const associateGroupsToSergeProject = async (projectId, groupIds, role = 'stakeholders') => {
  const { data } = await sergeApi.post(`/projects/${projectId}/groups`, {
    prospect_group_ids: groupIds,
    role,
  })
  return data
}

/**
 * Dissocie un groupe d'un projet Serge
 */
export const dissociateGroupFromSergeProject = async (projectId, groupId) => {
  const { data } = await sergeApi.delete(`/projects/${projectId}/groups/${groupId}`)
  return data
}

// ============================
// Utility functions
// ============================

/**
 * Vérifie si une étape est accessible
 */
export const isStepAccessible = (stepsStatus, step) => {
  return stepsStatus?.[step]?.isAccessible ?? false
}

/**
 * Vérifie si une étape est complétée
 */
export const isStepCompleted = (stepsStatus, step) => {
  return stepsStatus?.[step]?.status === 'completed'
}

/**
 * Obtient l'index de l'étape courante
 */
export const getCurrentStepIndex = (currentStep) => {
  return PROJECT_STEPS.indexOf(currentStep)
}

// getDeliverableLabel déjà défini plus haut (ligne 81)

// ============================
// Legal Entities
// ============================

/**
 * Liste toutes les entités légales
 */
export const getSergeLegalEntities = async () => {
  const { data } = await sergeApi.get('/legal-entities')
  return data['hydra:member'] || data
}

/**
 * Récupère une entité légale par ID
 */
export const getSergeLegalEntity = async (id) => {
  const { data } = await sergeApi.get(`/legal-entities/${id}`)
  return data
}

/**
 * Crée une nouvelle entité légale
 */
export const createSergeLegalEntity = async (payload) => {
  const { data } = await sergeApi.post('/legal-entities', payload)
  return data
}

/**
 * Met à jour une entité légale
 */
export const updateSergeLegalEntity = async (id, payload) => {
  const { data } = await sergeApi.put(`/legal-entities/${id}`, payload)
  return data
}

/**
 * Supprime une entité légale
 */
export const deleteSergeLegalEntity = async (id) => {
  const { data } = await sergeApi.delete(`/legal-entities/${id}`)
  return data
}

// ============================
// Recommended Document Order
// ============================

export const RECOMMENDED_DOCUMENT_ORDER = [
  { type: 'spec_functional', label: 'Spécifications fonctionnelles', phase: 'Analyse', order: 1 },
  { type: 'spec_technical', label: 'Spécifications techniques', phase: 'Analyse', order: 2 },
  { type: 'diagram_architecture', label: "Diagramme d'architecture", phase: 'Conception', order: 3 },
  { type: 'diagram_class', label: 'Diagramme de classe', phase: 'Conception', order: 4 },
  { type: 'diagram_sequence', label: 'Diagramme de séquence', phase: 'Conception', order: 5 },
  { type: 'user_stories', label: 'User Stories', phase: 'Conception', order: 6 },
  { type: 'risk_register', label: 'Registre des risques', phase: 'Gestion', order: 7 },
  { type: 'budget', label: 'Budget prévisionnel', phase: 'Gestion', order: 8 },
  { type: 'gantt_sheets', label: 'Planning Gantt (Google Sheets)', phase: 'Gestion', order: 9 },
  { type: 'master_service_agreement', label: 'Contrat cadre de prestation', phase: 'Légal', order: 10 },
  { type: 'payment_schedule', label: 'Échéancier de paiement', phase: 'Légal', order: 11 },
  { type: 'cgv', label: 'CGV', phase: 'Légal', order: 12 },
  { type: 'cgu', label: 'CGU', phase: 'Légal', order: 13 },
  { type: 'legal_notice', label: 'Mentions légales', phase: 'Légal', order: 14 },
  { type: 'privacy_policy', label: 'Politique de confidentialité', phase: 'Légal', order: 15 },
  { type: 'software_licenses', label: 'Liste des logiciels et licences', phase: 'Légal', order: 16 },
  { type: 'demo', label: 'Démo visuelle', phase: 'Livraison', order: 17 },
]

export default sergeApi
